import React, { lazy, Suspense } from 'react';
import { RoutePath } from '../global/enums/RoutePath';
import MainPage from '../pages/MainPage';
import { useCurrentUserContext } from '../context/currentUser/CurrentUserContext';
import GearLoader from './common/GearLoader';
import { useUiAdminConfigsContext } from '../context/uiAdminConfigs/UiAdminConfigsContext';
import { GEAR_UI_ADMIN_CONFIG_TYPE } from '../Model/uiAdminConfig/GearUiAdminConfigType';
import { GearMessageBar } from './common/GearMessageBar';
import { GearMaintenancePageState } from '../Model/GearMaintenancePageState';
import { AccessDeniedPage } from '../pages/AccessDeniedPage';
import { Navigate, Route, Routes } from 'react-router-dom';

const SettingsPage = lazy(() => import('../pages/SettingsPage'));
const ItAdminSettingsPage = lazy(() => import('../pages/ItAdminSettingsPage'));
const MaintenancePage = lazy(() => import('../pages/MaintenancePage'));
const LogoutPage = lazy(() => import('../pages/LogoutPage'));
const NoMatchPage = lazy(() => import('../pages/NoMatchPage'));

const Root: React.FC = () => {
  const { currentUser, isLoading } = useCurrentUserContext();
  const { uiAdminConfigState, uiAdminConfigWritePermissions, setConfigWritePermissionsKeys } =
    useUiAdminConfigsContext();

  React.useEffect(() => {
    if (setConfigWritePermissionsKeys) {
      setConfigWritePermissionsKeys([GEAR_UI_ADMIN_CONFIG_TYPE.MAINTENANCE_PAGE]);
    }
  }, [setConfigWritePermissionsKeys]);

  if (isLoading || !uiAdminConfigWritePermissions || !uiAdminConfigState.maintenancePage) {
    return <GearLoader />;
  }

  if (!currentUser || currentUser.indexes?.length === 0) {
    return (
      <Suspense fallback={<GearLoader />}>
        <Routes>
          <Route path="*" element={<AccessDeniedPage />} />
        </Routes>
      </Suspense>
    );
  }

  if (
    !uiAdminConfigWritePermissions.maintenancePage &&
    (uiAdminConfigState.maintenancePage as GearMaintenancePageState)?.enabled
  ) {
    return (
      <Suspense fallback={<GearLoader />}>
        <Routes>
          <Route path="*" element={<MaintenancePage />} />
        </Routes>
      </Suspense>
    );
  }

  return (
    <>
      <GearMessageBar />
      <Suspense fallback={<GearLoader />}>
        <Routes>
          <Route path={RoutePath.HOME} element={<MainPage />} />
          <Route path={RoutePath.SETTINGS}>
            <Route index element={<Navigate to={RoutePath.SETTINGS_VIEWS} replace />} />
            <Route path={RoutePath.SETTINGS_VIEWS} element={<SettingsPage />} />
            <Route path={RoutePath.SETTINGS_HELP_CENTER} element={<SettingsPage />} />
            <Route path={RoutePath.SETTINGS_ADMIN_CENTER} element={<SettingsPage />} />
            <Route path={RoutePath.SETTINGS_IT_ADMIN_CENTER} element={<ItAdminSettingsPage />} />
          </Route>
          <Route path={RoutePath.LOGOUT} element={<LogoutPage />} />
          <Route path={RoutePath.MAINTENANCE} element={<MaintenancePage />} />
          <Route path="*" element={<NoMatchPage />} />
        </Routes>
      </Suspense>
    </>
  );
};

export default Root;
